import { formatSecondsToHHMMSS } from "@utils/utils";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useBcbaCommentsDistributionRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface CommentDistributionRawDataProps {
  isCommentDistributionWithCommentType: boolean;
}
type CommentDistributionRawDataType = {
  clientId: number;
  clientName: string;
  sessionDate: string;
  sessionDatetime: string;
  sessionDuration: number;
  totalComments: number;
  bcbaName: string;
  commentTypeCounts: {
    love: number;
    praise: number;
    question: number;
    feedback: number;
    totalComments: number;
  };
};
export const CommentDistributionRawData: React.FC<CommentDistributionRawDataProps> = ({
  isCommentDistributionWithCommentType,
}) => {
  const { selectedDates, selectedBCBA: selectedBCBAList } = useVideoAnalytics();
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedDates);
  const [selectedBCBA, setSelectedBCBA] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedBCBA, selectedClient]);

  const { data, isFetching } = useBcbaCommentsDistributionRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      bcbaProviderIds: selectedBCBA.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
    },
    {
      queryKey: ["bcbaCommentsDistributionRawData", dates, selectedBCBA, pageNo, pageSize, selectedClient],
    },
  );

  const columns: Column<CommentDistributionRawDataType>[] = [
    {
      title: "Client ID",
      key: "clientId",
      dataIndex: "clientId",
    },
    {
      title: "Client",
      key: "clientName",
      dataIndex: "clientName",
      render: (value) => <span className="ph-no-capture">{(value as string) || "-"}</span>,
    },
    {
      title: "Session Date & Time",
      key: "sessionDatetime",
      dataIndex: "sessionDatetime",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Session Duration",
      key: "sessionDuration",
      dataIndex: "sessionDuration",
      render: (value) => {
        return value ? formatSecondsToHHMMSS(value as number) : "-";
      },
    },
    {
      title: "BCBA",
      key: "bcba",
      dataIndex: "bcbaName",
    },
    {
      title: "Love",
      key: "love",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.love ?? 0,
    },
    {
      title: "Praise",
      key: "praise",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.praise ?? 0,
    },
    {
      title: "Feedback",
      key: "feedback",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.feedback ?? 0,
    },
    {
      title: "Question",
      key: "question",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.question ?? 0,
    },
    {
      title: "Total Comments",
      key: "totalComments",
      dataIndex: "totalComments",
    },
  ];

  const filteredColumn = isCommentDistributionWithCommentType
    ? columns
    : columns.filter((item) => item.dataIndex !== "commentTypeCounts");

  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedBCBA={selectedBCBA}
        setSelectedBCBA={setSelectedBCBA}
        selectedClients={selectedClient}
        setSelectedClients={setSelectedClient}
        showRBTFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.bcbaCommentsDistributionRawData?.rawData
            ? (data.bcbaCommentsDistributionRawData?.rawData as CommentDistributionRawDataType[])
            : []
        }
        columns={filteredColumn}
        loading={isFetching}
        totalItemCount={data?.bcbaCommentsDistributionRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
